import { useCart } from '@/data/Content/Cart';
import { useNotifications } from '@/data/Content/Notifications';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useUser } from '@/data/User';
import { Switch } from '@/utils/switch';
import { Breakpoint } from '@mui/material';
import { FC, MouseEventHandler, useCallback, useEffect, useRef, useState } from 'react';
import { HeaderMiniCartDesktop } from '@/components/content/Header/parts/HeaderMiniCartDesktop';
import { HeaderMiniCartMobile } from '@/components/content/Header/parts/HeaderMiniCartMobile';
import { useMobileHeaderQuery } from '..';
import { FreeShippingActivatedPopup } from '@/components/blocks/FreeShippingActivatedPopup';
import {
	FreeShippingActivatedPopupRecommendationProps,
	useFreeShippingActivatedPopupRecommendation,
} from '@/data/Content/FreeShippingActivatedPopupRecommendation';

type Props = {
	mobileBreakpoint: Breakpoint;
};

type ActiveFreeShippingActivatedPopup = {
	key: number;
	props: FreeShippingActivatedPopupRecommendationProps;
	resumeCheckout: () => void;
};

export const HeaderMiniCart: FC<Props> = () => {
	const router = useNextRouter();
	const { message } = useNotifications();
	const { user: { userId } = {} } = useUser();
	const { count: itemCount, mutateCart } = useCart();
	const isMobile = useMobileHeaderQuery();
	const [open, setOpen] = useState(false);
	const [fromLocal, setFromLocal] = useState(false);
	const initialized = useRef<boolean>(false);
	const [count, setCount] = useState<number>(0);
	const [activeFreeShippingActivatedPopup, setActiveFreeShippingActivatedPopup] =
		useState<ActiveFreeShippingActivatedPopup>();
	const { getFreeShippingActivatedPopupProps } = useFreeShippingActivatedPopupRecommendation();

	const handleToolTip = useCallback(
		(action?: string) => () =>
			setOpen((open) =>
				Switch(action)
					.case('open', () => true)
					.case('close', () => false)
					.defaultTo(() => !open)
			),
		[]
	);
	const onMouseEnter: MouseEventHandler<HTMLDivElement> = useCallback(() => {
		if (!isMobile) {
			handleToolTip('open')();
			setFromLocal(true);
		}
	}, [handleToolTip, isMobile]);
	useEffect(() => {
		if (message.isAddToCart) {
			setOpen(true);
			setFromLocal(false);
		}
	}, [message.isAddToCart, router.basePath, message]);

	useEffect(() => {
		if (!router.asPath) return;
		handleToolTip('close')();
		setFromLocal(false);
	}, [handleToolTip, router.asPath]);

	useEffect(() => {
		if (initialized.current) {
			mutateCart();
		} else {
			initialized.current = true;
		}
	}, [mutateCart, userId]);

	// for CDN caching -- render this on client
	useEffect(() => {
		setCount(() => itemCount);
	}, [itemCount]);

	// useEffect(() => touchMoveListener(handleToolTip('close')), []); // eslint-disable-line react-hooks/exhaustive-deps

	const onOpen = useCallback(() => {
		handleToolTip('open')();
		setFromLocal(true);
	}, [handleToolTip]);

	const onOpenFreeShippingActivatedPopup = useCallback(
		async (resumeCheckout: () => void) => {
			const props = await getFreeShippingActivatedPopupProps();

			if (!props) {
				return false;
			}

			setActiveFreeShippingActivatedPopup({
				key: Date.now(),
				props,
				resumeCheckout,
			});

			return true;
		},
		[getFreeShippingActivatedPopupProps]
	);

	if (isMobile) {
		return (
			<>
				<HeaderMiniCartMobile
					open={open}
					fromLocal={fromLocal}
					count={count}
					handleToolTip={handleToolTip}
					onOpen={onOpen}
					onOpenFreeShippingActivatedPopup={onOpenFreeShippingActivatedPopup}
				/>
				{activeFreeShippingActivatedPopup ? (
					<FreeShippingActivatedPopup
						key={activeFreeShippingActivatedPopup.key}
						{...activeFreeShippingActivatedPopup.props}
						onUnavailable={activeFreeShippingActivatedPopup.resumeCheckout}
					/>
				) : null}
			</>
		);
	}

	return (
		<>
			<HeaderMiniCartDesktop
				open={open}
				fromLocal={fromLocal}
				count={count}
				handleToolTip={handleToolTip}
				onMouseEnter={onMouseEnter}
				onOpenFreeShippingActivatedPopup={onOpenFreeShippingActivatedPopup}
			/>
			{activeFreeShippingActivatedPopup ? (
				<FreeShippingActivatedPopup
					key={activeFreeShippingActivatedPopup.key}
					{...activeFreeShippingActivatedPopup.props}
					onUnavailable={activeFreeShippingActivatedPopup.resumeCheckout}
				/>
			) : null}
		</>
	);
};
